import type { DirectHireInput } from '../hire-types'
import { useState } from 'react'
import { Platform } from 'react-native'
import { useRouter } from 'expo-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Text, YStack } from 'tamagui'
import { api } from '../../../api/client'
import { useApp } from '../../../i18n'
import { pickFileWeb } from '../../../lib/files'
import { Button, DateField, FormInput, FormTextArea } from '../../../ui'
import { EmploymentField, useEmploymentSelection } from './EmploymentField'
import { Field } from './shared'

export function DirectHireStart() {
  const { t } = useApp()
  const router = useRouter()
  const qc = useQueryClient()
  const employment = useEmploymentSelection()
  // Stable per form; used for retry identity, not as an authorization token.
  const [requestId] = useState(() => `hire-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`)
  const [approvedBy, setApprovedBy] = useState('')
  const [approvedOn, setApprovedOn] = useState('')
  const [basis, setBasis] = useState('')
  const [proof, setProof] = useState<{ file: File; uploadId?: string } | null>(null)
  const access = useQuery({
    queryKey: ['direct-hire-access', employment.employmentId],
    queryFn: () => api.directHireAccess(employment.employmentId),
    enabled: employment.ready,
    retry: false,
  })
  const create = useMutation({
    mutationFn: async (data: DirectHireInput) => {
      let uploadId = proof?.uploadId
      if (proof && !uploadId) {
        const form = new FormData()
        form.append('file', proof.file as unknown as Blob)
        uploadId = (await api.uploadEdoDoc(form)).id
        // A failed creation can be retried without uploading the same proof again.
        setProof({ file: proof.file, uploadId })
      }
      return api.prepareDirectHire({ ...data, uploadIds: uploadId ? [uploadId] : [] })
    },
    onSuccess: req => {
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
      router.push(`/processes/${req.id}`)
    },
  })
  const allowed = employment.ready && access.isSuccess && access.data.canPrepare
  const ready = allowed && !!approvedBy.trim() && !!approvedOn && !!basis.trim() && !create.isPending
  return <YStack gap={12}>
    <Text fontWeight="700">{t({ ru: 'Приём без подбора', en: 'Hire without recruitment' })}</Text>
    <Text color="$muted">{t({ ru: 'Решение о найме уже согласовано вне портала. Укажите основание, затем заполните данные сотрудника. Далее — кадровая проверка и подпись.', en: 'The hiring decision was approved outside the portal. Record the approval, then complete the employee details. HR review and signature follow.' })}</Text>
    <YStack pointerEvents={create.isPending ? 'none' : 'auto'}><EmploymentField selection={employment} /></YStack>
    {employment.ready && access.isPending && <Text>{t({ ru: 'Проверка прав…', en: 'Checking access…' })}</Text>}
    {access.error instanceof Error && <Text color="$red10">{access.error.message}</Text>}
    {employment.ready && access.isSuccess && !access.data.canPrepare && <Text>{t({ ru: 'Оформление приёма доступно кадровику выбранной компании или администратору.', en: 'Only HR of the selected company or an administrator can prepare a hire.' })}</Text>}
    {allowed && <>
      <Field label={t({ ru: 'Кем согласовано', en: 'Approved by' })} required><FormInput placeholder={t({ ru: 'Кем согласован приём', en: 'Who approved the hire' })} value={approvedBy} maxLength={200} editable={!create.isPending} onChangeText={setApprovedBy} /></Field>
      <Field label={t({ ru: 'Дата согласования', en: 'Approval date' })} required><DateField min="1900-01-01" value={approvedOn} onChange={setApprovedOn} /></Field>
      <Field label={t({ ru: 'Основание приёма', en: 'Approval basis' })} required><FormTextArea placeholder={t({ ru: 'Основание согласованного решения', en: 'Details of the approved decision' })} value={basis} maxLength={2000} editable={!create.isPending} onChangeText={setBasis} /></Field>
      {Platform.OS === 'web' ? <Button disabled={create.isPending} onPress={async () => { const file = await pickFileWeb('.pdf,.jpg,.jpeg,.png,.doc,.docx'); if (file) setProof({ file }) }}>{t({ ru: 'Приложить подтверждение (необязательно)', en: 'Attach approval evidence (optional)' })}</Button> : <Text color="$muted">{t({ ru: 'Подтверждающий документ можно приложить при создании в веб-версии.', en: 'Approval evidence can be attached when creating the draft in the web version.' })}</Text>}
      {proof && <YStack gap={4}><Text>{proof.file.name}</Text><Button disabled={create.isPending} onPress={() => setProof(null)}>{t({ ru: 'Убрать документ', en: 'Remove document' })}</Button></YStack>}
      <Button variant="primary" disabled={!ready} onPress={() => { if (ready) create.mutate({ requestId, employmentId: employment.employmentId, approvedBy: approvedBy.trim(), approvedOn, basis: basis.trim() }) }}>{t({ ru: 'Создать черновик приёма', en: 'Create hire draft' })}</Button>
      {create.error instanceof Error && <Text color="$red10">{create.error.message}</Text>}
    </>}
  </YStack>
}
